import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TableProcessingOptions } from '@/components/documents/TableProcessingOptions'
import { isDocumentInFlight } from '@/features/documents/audio-stage'
import { tableProcessingKey } from '@/features/documents/table-processing'
import { caseKeys } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import type { CaseDocumentDetail, TabularExtractionMode } from '@/services/api/cases'
import { errorMessage } from '@/utils/errors'

export type TableProcessingDocument = Pick<CaseDocumentDetail, 'id' | 'filename'> &
  Partial<
    Pick<CaseDocumentDetail, 'tabular_extraction_mode' | 'member_role' | 'is_capture' | 'status'>
  >

export function DocumentTableProcessing({
  caseId,
  document,
}: {
  caseId: string
  document: TableProcessingDocument
}) {
  const { t } = useTranslation()
  const mutation = useSaveTableProcessing(caseId, document.id)
  const mayEdit = document.member_role === 'owner' || document.member_role === 'collaborator'
  const disabled =
    !mayEdit ||
    document.is_capture ||
    isDocumentInFlight(document.status ?? '') ||
    mutation.isPending
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
      <TableProcessingOptions
        value={document.tabular_extraction_mode ?? 'structured'}
        onChange={mode => mutation.mutate(mode)}
        disabled={disabled}
      />
      <p className="text-xs text-muted-foreground" role="status">
        {t(
          mutation.isPending
            ? 'structuredIngest.processing.saving'
            : 'structuredIngest.processing.nextRun'
        )}
      </p>
      {mutation.error && (
        <p role="alert" className="text-xs text-destructive">
          {errorMessage(mutation.error, t('structuredIngest.processing.failed'))}
        </p>
      )}
    </div>
  )
}

function useSaveTableProcessing(caseId: string, documentId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (mode: TabularExtractionMode) =>
      api.cases.updateTableProcessing(caseId, documentId, mode),
    onSuccess: async result => {
      client.setQueryData(tableProcessingKey(caseId, documentId), result)
      client.setQueryData<CaseDocumentDetail>(caseKeys.documentDetail(caseId, documentId), file =>
        file ? { ...file, tabular_extraction_mode: result.tabular_extraction_mode } : file
      )
      await client.invalidateQueries({ queryKey: caseKeys.documentDetail(caseId, documentId) })
    },
  })
}
